import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useSimpleToast, getErrorMessage } from '../components/Toast';
import { FileText, Download, Send, Users, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DocumentGeneration({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docType, setDocType] = useState('appointment');
  const [idCardData, setIdCardData] = useState({
    photo_url: '',
    employee_signature: ''
  });

  useEffect(() => {
    fetchEmployees();
    fetchGeneratedDocs();
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchGeneratedDocs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/document-gen/generated`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedDocs(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const generateDocument = async () => {
    if (!selectedEmployee) {
      showToast('Please select an employee', 'warning');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (docType === 'id-card') {
        // Generate ID Card
        const response = await axios.post(`${API}/document-gen/generate-id-card`, {
          employee_id: selectedEmployee.id,
          employee_name: selectedEmployee.name,
          employee_code: selectedEmployee.employee_code,
          designation: selectedEmployee.designation || 'Employee',
          department: selectedEmployee.departments?.[0] || selectedEmployee.department || 'General',
          blood_group: selectedEmployee.blood_group || 'O+',
          emergency_contact: selectedEmployee.emergency_contact || selectedEmployee.mobile_number || '',
          photo_url: idCardData.photo_url,
          employee_signature: idCardData.employee_signature
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        showToast('ID Card Generated!', 'success');
        fetchGeneratedDocs();
        setSelectedEmployee(null);
        return;
      }

      const payload = {
        employee_id: selectedEmployee.id,
        employee_name: selectedEmployee.name,
        employee_code: selectedEmployee.employee_code,
        designation: selectedEmployee.designation || 'Employee',
        reporting_to: selectedEmployee.reporting_manager || 'HR Manager',
        location: selectedEmployee.location || 'Head Office',
        notice_period_days: 30,
        employment_type: 'full-time'
      };

      const endpoint = docType === 'appointment' 
        ? '/document-gen/generate-appointment-letter' 
        : '/document-gen/generate-offer-letter';

      const response = await axios.post(`${API}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showToast(response.data.message || 'Document generated!', 'success');
      fetchGeneratedDocs();
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Document generation error:', error);
      showToast(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/document-gen/download/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast('Download started!', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showToast('Error downloading: ' + error.message, 'error');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Document Generation</h1>
          <p className="text-gray-600 mt-2">Generate HR documents (Appointment Letters, Offer Letters, Annexures)</p>
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">📄 Professional Document Generation</h3>
          <p className="text-sm text-blue-800">
            Generate official HR documents using Zobox templates with company letterhead. All documents are auto-populated with employee details.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Employees</p>
                <p className="text-3xl font-bold" style={{color: '#2b3380'}}>{employees.length}</p>
              </div>
              <Users className="w-12 h-12 text-blue-200" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Generated Docs</p>
                <p className="text-3xl font-bold text-green-600">{generatedDocs.length}</p>
              </div>
              <FileText className="w-12 h-12 text-green-200" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Templates Available</p>
                <p className="text-3xl font-bold text-purple-600">5+</p>
              </div>
              <CheckCircle className="w-12 h-12 text-purple-200" />
            </div>
          </div>
        </div>

        {/* Generation Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Generate New Document</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Document Type</label>
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="appointment">Appointment Letter</option>
                <option value="offer">Offer Letter</option>
                <option value="id-card">ID Card</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Select Employee</label>
              <select
                value={selectedEmployee?.id || ''}
                onChange={(e) => {
                  const emp = employees.find(emp => emp.id === e.target.value);
                  if (emp) {
                    setSelectedEmployee({
                      ...emp,
                      name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.employee_id,
                      employee_code: emp.employee_id || emp.id,
                      location: emp.work_location || emp.office_branch || 'Head Office',
                      reporting_manager: emp.reporting_manager_id || 'HR Manager'
                    });
                  } else {
                    setSelectedEmployee(null);
                  }
                }}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">-- Choose Employee --</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} ({emp.employee_id || emp.id}) - {emp.designation || 'N/A'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedEmployee && (
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <h3 className="font-semibold mb-2">Selected Employee Details:</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-medium">Name:</span> {selectedEmployee.name}</div>
                <div><span className="font-medium">Code:</span> {selectedEmployee.employee_code}</div>
                <div><span className="font-medium">Designation:</span> {selectedEmployee.designation || 'N/A'}</div>
                <div><span className="font-medium">Department:</span> {selectedEmployee.departments?.[0] || selectedEmployee.department || 'N/A'}</div>
                <div><span className="font-medium">Location:</span> {selectedEmployee.location}</div>
              </div>
            </div>
          )}

          <button
            onClick={generateDocument}
            disabled={loading || !selectedEmployee}
            className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
            style={{backgroundColor: (loading || !selectedEmployee) ? '#9e9e9e' : '#2b3380'}}
          >
            <Send className="w-5 h-5" />
            {loading ? 'Generating...' : `Generate ${
              docType === 'appointment' ? 'Appointment Letter' : 
              docType === 'offer' ? 'Offer Letter' : 
              'ID Card'
            }`}
          </button>
        </div>

        {/* Generated Documents */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Generated Documents</h2>
          
          {generatedDocs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No documents generated yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Document Name</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Size</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Created At</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {generatedDocs.map((doc, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{doc.name}</td>
                      <td className="px-4 py-3 text-sm">{(doc.size / 1024).toFixed(2)} KB</td>
                      <td className="px-4 py-3 text-sm">{new Date(doc.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => downloadDocument(doc.name)}
                          className="flex items-center gap-2 px-3 py-1 rounded-lg text-white"
                          style={{backgroundColor: '#2b3380'}}
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default DocumentGeneration;
